import { useState, useEffect } from "react";
import { BetaBadge } from "./BetaBadge";
import { KalibLogo } from "./KalibLogo";

const RECALIB_MESSAGES = [
  "Picking up your new profile...",
  "Resolving the implication matrix for your combination...",
  "Re-ranking signals for your altitude...",
  "Rewriting the actions for how you actually sell...",
  "Cross-checking cross-vendor differentiation...",
  "Almost there — polishing the framing...",
];

/**
 * Wave 9.1 — shown after a profile save so users feel the recalibration is
 * being done specifically for them. Runs for ~3.5s before the real Reveal fades in.
 *
 * Props:
 *  - summary: { roleLabel, seniorityLabel, companyLabel }
 *  - durationMs: number (default 3500)
 *  - onComplete: () => void
 */
export default function RecalibratingOverlay({ summary = {}, durationMs = 3500, onComplete }) {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const msgInterval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % RECALIB_MESSAGES.length);
    }, Math.max(durationMs / RECALIB_MESSAGES.length, 400));

    const tickMs = 80;
    const increment = (100 / durationMs) * tickMs;
    const progInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + increment, 99));
    }, tickMs);

    const done = setTimeout(() => {
      setProgress(100);
      if (onComplete) onComplete();
    }, durationMs);

    return () => {
      clearInterval(msgInterval);
      clearInterval(progInterval);
      clearTimeout(done);
    };
  }, [durationMs, onComplete]);

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="recalibrating-overlay">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <KalibLogo variant="light" height={28} testId="recalibrating-overlay-logo" />
            <BetaBadge />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <p className="uppercase mb-3" style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.18em", color: "#B45309" }}>
            Recalibrating for you
          </p>
          <h2 className="text-2xl font-medium tracking-tight text-zinc-950 font-heading mb-3" data-testid="recalibrating-title">
            Rebuilding your Reveal
          </h2>
          {/* 2026-05-03 — "Now viewing as <role> · <seniority>" line removed
              for parity with desktop. The universal "Viewing as:" strip on
              the reveal page is the single source of truth for the active
              lens. The overlay only confirms the rebuild is in progress. */}
          <div className="mb-8" />

          <div className="w-full h-1 bg-zinc-200 mb-6">
            <div
              className="h-1 bg-amber-700 transition-all duration-200"
              style={{ width: `${progress}%` }}
              data-testid="recalibrating-progress-bar"
            />
          </div>

          <div className="h-12 flex items-center justify-center">
            <p
              key={currentMessage}
              className="text-xs text-zinc-900 animate-fade-in"
              data-testid="recalibrating-trivia"
            >
              {RECALIB_MESSAGES[currentMessage]}
            </p>
          </div>

          <p className="text-[11px] text-zinc-900 mt-6 italic">
            Every signal, every action below is being re-ranked for your exact combination.
          </p>
        </div>
      </div>
    </div>
  );
}
